import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  AddHome,
  Edit,
  Delete,
  Save,
  Warning,
  Info
} from '@mui/icons-material';
import SecuredLayout from '../components/layouts/SecuredLayout';
import userService from '../services/userService';

const AddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [newAddress, setNewAddress] = useState({
    name: '',
    addressline1: '',
    addressline2: '',
    area: '',
    state: '',
    postal_code: '',
    city: '',
    landmark: ''
  });

  // Fetch addresses from API
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const response = await userService.getAllAddress();
      setAddresses(response.data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load addresses. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleAddAddress = async () => {
    // Validate required fields
    const errors = {};
    if (!newAddress.landmark || newAddress.landmark.trim() === '') {
      errors.landmark = 'This field may not be blank.';
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'error'
      });
      return;
    }

    setIsUpdate(true);
    setFieldErrors({});
    
    try {
      const response = await userService.addAddress(newAddress);
      
      // Refresh the addresses list
      await fetchAddresses();
      
      setNewAddress({
        name: '',
        addressline1: '',
        addressline2: '',
        area: '',
        state: '',
        postal_code: '',
        city: '',
        landmark: ''
      });
      setAddDialogOpen(false);
      setFieldErrors({});
      
      setSnackbar({
        open: true,
        message: 'Address added successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding address:', error);
      
      // Handle API validation errors
      if (error.response?.data) {
        const apiErrors = error.response.data;
        const fieldValidationErrors = {};
        
        // Parse field-level errors from API response
        Object.keys(apiErrors).forEach(field => {
          if (Array.isArray(apiErrors[field])) {
            fieldValidationErrors[field] = apiErrors[field][0];
          } else if (typeof apiErrors[field] === 'string') {
            fieldValidationErrors[field] = apiErrors[field];
          }
        });
        
        if (Object.keys(fieldValidationErrors).length > 0) {
          setFieldErrors(fieldValidationErrors);
        }
        
        // Show general error message
        const firstError = Object.values(fieldValidationErrors)[0] || 'Failed to add address. Please try again.';
        setSnackbar({
          open: true,
          message: firstError,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to add address. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setIsUpdate(false);
    }
  };

  const handleEditAddress = (address) => {
    setNewAddress(address);
    setFieldErrors({});
    setEditDialogOpen(true);
  };

  const handleUpdateAddress = async () => {
    // Validate required fields
    const errors = {};
    if (!newAddress.landmark || newAddress.landmark.trim() === '') {
      errors.landmark = 'This field may not be blank.';
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'error'
      });
      return;
    }

    setIsUpdate(true);
    setFieldErrors({});
    
    try {
      const response = await userService.updateAddress(newAddress.id, newAddress);
      
      // Refresh the addresses list
      await fetchAddresses();
      
      setEditDialogOpen(false);
      setNewAddress({
        name: '',
        addressline1: '',
        addressline2: '',
        area: '',
        state: '',
        postal_code: '',
        city: '',
        landmark: ''
      });
      setFieldErrors({});
      
      setSnackbar({
        open: true,
        message: 'Address updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating address:', error);
      
      // Handle API validation errors
      if (error.response?.data) {
        const apiErrors = error.response.data;
        const fieldValidationErrors = {};
        
        // Parse field-level errors from API response
        Object.keys(apiErrors).forEach(field => {
          if (Array.isArray(apiErrors[field])) {
            fieldValidationErrors[field] = apiErrors[field][0];
          } else if (typeof apiErrors[field] === 'string') {
            fieldValidationErrors[field] = apiErrors[field];
          }
        });
        
        if (Object.keys(fieldValidationErrors).length > 0) {
          setFieldErrors(fieldValidationErrors);
        }
        
        // Show general error message
        const firstError = Object.values(fieldValidationErrors)[0] || 'Failed to update address. Please try again.';
        setSnackbar({
          open: true,
          message: firstError,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to update address. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setIsUpdate(false);
    }
  };

  const handleDeleteConfirm = (id, name) => {
    setDeleteId(id);
    setDeleteName(name);
    setDeleteDialog(true);
  };

  const handleDeleteAddress = async () => {
    try {
      const response = await userService.deleteAddress(deleteId);
      
      // Refresh the addresses list
      await fetchAddresses();
      
      setDeleteDialog(false);
      
      setSnackbar({
        open: true,
        message: 'Address deleted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting address:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete address. Please try again.',
        severity: 'error'
      });
    }
  };

  const resetForm = () => {
    setNewAddress({
      name: '',
      addressline1: '',
      addressline2: '',
      area: '',
      state: '',
      postal_code: '',
      city: '',
      landmark: ''
    });
    setFieldErrors({});
  };

  const renderAddressForm = (isEdit = false) => (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={2}>
        <TextField
          fullWidth
          label="Address Name"
          placeholder="e.g., Home, Office, Apartment"
          value={newAddress.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Address Line 1"
          placeholder="Street address, building name"
          value={newAddress.addressline1}
          onChange={(e) => handleInputChange('addressline1', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Address Line 2"
          placeholder="Apartment, suite, unit (optional)"
          value={newAddress.addressline2}
          onChange={(e) => handleInputChange('addressline2', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Area/Locality"
          placeholder="Area or locality name"
          value={newAddress.area}
          onChange={(e) => handleInputChange('area', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="City"
          placeholder="City name"
          value={newAddress.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="State"
          placeholder="State or province"
          value={newAddress.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Pincode"
          placeholder="6-digit postal code"
          value={newAddress.postal_code}
          onChange={(e) => handleInputChange('postal_code', e.target.value)}
          variant="outlined"
        />
        
        <TextField
          fullWidth
          required
          label="Delivery Instructions"
          placeholder="Special delivery instructions, landmarks, or notes for the delivery person"
          value={newAddress.landmark}
          onChange={(e) => handleInputChange('landmark', e.target.value)}
          error={!!fieldErrors.landmark}
          helperText={fieldErrors.landmark || 'Landmark or delivery instructions are required'}
          variant="outlined"
          multiline
          rows={3}
        />
      </Stack>
    </Box>
  );

  return (
    <SecuredLayout 
      activeTab={1} 
      pageTitle="Address Book"
      contentCols={10}
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header Section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            bgcolor: 'primary.main',
            borderRadius: 2
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
            Address Book
          </Typography>
          <Typography variant="body1" sx={{ color: 'white' }}>
            Manage your delivery addresses
          </Typography>
        </Paper>

        {/* Add Address Button */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddHome />}
            onClick={() => {
              resetForm();
              setAddDialogOpen(true);
            }}
          >
            Add New Address
          </Button>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Addresses Table */}
        {!isLoading && addresses.length > 0 && (
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Address</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>City</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pincode</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Instructions</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addresses.map((address) => (
                  <TableRow key={address.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {address.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {address.addressline1}
                        {address.addressline2 && `, ${address.addressline2}`}
                        {address.area && `, ${address.area}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {address.city || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {address.state || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {address.postal_code || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {address.landmark || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<Edit />}
                          onClick={() => handleEditAddress(address)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteConfirm(address.id, address.name)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No addresses message */}
        {!isLoading && addresses.length === 0 && (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h5" color="text.primary" gutterBottom>
              No Addresses Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Add your first delivery address to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddHome />}
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
            >
              Add Your First Address
            </Button>
          </Paper>
        )}

        {/* Add Address Dialog */}
        <Dialog 
          open={addDialogOpen} 
          onClose={() => setAddDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white'
          }}>
            Add New Address
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {renderAddressForm(false)}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                setAddDialogOpen(false);
                resetForm();
              }} 
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddAddress}
              disabled={isUpdate}
              variant="contained"
              color="primary"
              startIcon={isUpdate ? <CircularProgress size={20} /> : <Save />}
            >
              {isUpdate ? 'Adding...' : 'Add Address'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Address Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white'
          }}>
            Edit Address
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {renderAddressForm(true)}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                setEditDialogOpen(false);
                resetForm();
              }} 
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateAddress}
              disabled={isUpdate}
              variant="contained"
              color="primary"
              startIcon={isUpdate ? <CircularProgress size={20} /> : <Save />}
            >
              {isUpdate ? 'Updating...' : 'Update Address'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialog} 
          onClose={() => setDeleteDialog(false)} 
          maxWidth="sm"
        >
          <DialogTitle sx={{ 
            bgcolor: 'error.main', 
            color: 'white'
          }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1">
              Are you sure you want to delete the <strong>{deleteName}</strong> address? 
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setDeleteDialog(false)} 
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteAddress} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </SecuredLayout>
  );
};

export default AddressBook;
